import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Breadcrumb,
  Button,
  Card,
  Collapse,
  Empty,
  Input,
  message,
  Modal,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import config from "../../api_config.json";
import { useAppContext } from "../../context";
import {
  BorderlessTableOutlined,
  CopyOutlined,
  EditOutlined,
  HolderOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import ReactJson from "@microlink/react-json-view";
import ModuleEditModal from "./ModuleEditModal";
import ParseLogo from "../../images/logos/parse.png";
import {
  buildModuleCatalogData,
  compareByOrderAndName,
  ANOMALY_META,
} from "./moduleCatalogUtils";
import { isRecentlyChanged, RECENT_DAYS } from "./recentUtils";

const { Title, Text } = Typography;

const WARN_COLOR = "#d46b08";
const NONE_SECTION = "__none__";

const query = `
  query fetchModules($where: ModuleWhereInput) {
    modules(where: $where, first: 10000) {
      edges {
        node {
          objectId
          name
          description
          html_description
          updatedAt
          createdAt
          icon {
            name
            url
          }
          module_category_pointer {
            objectId
            name
            order
            color
            darker_color
          }
          included_service_relation {
            edges {
              node {
                objectId
                name
              }
            }
          }
          related_service_relation {
            edges {
              node {
                objectId
                name
              }
            }
          }
          target_relation {
            edges {
              node {
                objectId
                name
              }
            }
          }
          power_by
          power_by_link
          una_tantum
          quantity
          included_quantity
          charge_by_quantity
          minutes_of_training
          price
          price_for_quantity
          is_product_line
          sku
          order
          is_active
          included_quantity_max_step
          included_quantity_max_step_multiplier
          included_quantity_max_step_minimum
          is_main
          unit_of_measure
          youtube_video_url
          landing_page_url
          cover_image_url
          features {
            ... on Element {
              value
            }
          }
        }
      }
    }
  }
`
  .replace(/\r?\n|\r/g, "")
  .replace(/\t/g, " ");

const updateModuleOrderMutation = `
  mutation UpdateModuleOrder($id: ID!, $fields: UpdateModuleFieldsInput!) {
    updateModule(input: { id: $id, fields: $fields }) {
      module {
        objectId
        order
      }
    }
  }
`
  .replace(/\r?\n|\r/g, "")
  .replace(/\t/g, " ");

const ROLE_FILTER_OPTIONS = [
  { label: "Tutti", value: "" },
  { label: "Principali", value: "main" },
  { label: "Aggiuntivi", value: "addon" },
];

const getPriceLabel = (price) =>
  price ? `${price.toFixed(2).replace(".", ",")} €` : null;

const getNumericOrder = (value) => {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
};

const getOrderDisplay = (value) => {
  const normalized = getNumericOrder(value);
  return normalized === null ? "-" : normalized;
};

const getNextOrderValue = (modules = []) => {
  const numericOrders = modules
    .map((module) => getNumericOrder(module?.order))
    .filter((order) => order !== null);

  if (numericOrders.length === 0) {
    return 10;
  }

  return Math.max(...numericOrders) + 10;
};

const getCategorySeed = (category) => {
  if (!category?.objectId || category.objectId === "uncategorized") {
    return undefined;
  }

  return {
    objectId: category.objectId,
    name: category.name,
  };
};

const buildModuleOrderUpdates = (modulesScope, fromIndex, toIndex) => {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= modulesScope.length ||
    toIndex >= modulesScope.length ||
    fromIndex === toIndex
  ) {
    return [];
  }

  const reorderedModules = arrayMove(modulesScope, fromIndex, toIndex);
  const startIndex = Math.min(fromIndex, toIndex);
  const endIndex = Math.max(fromIndex, toIndex);
  const originalOrders = modulesScope
    .slice(startIndex, endIndex + 1)
    .map((module) => getNumericOrder(module.order));

  const canReuseOrderRange = originalOrders.every(
    (order, index) =>
      order !== null && (index === 0 || order > originalOrders[index - 1])
  );

  if (canReuseOrderRange) {
    return reorderedModules
      .slice(startIndex, endIndex + 1)
      .map((module, index) => ({
        id: module.objectId,
        order: originalOrders[index],
        previousOrder: getNumericOrder(module.order),
      }))
      .filter((update) => update.previousOrder !== update.order);
  }

  return reorderedModules
    .map((module, index) => ({
      id: module.objectId,
      order: (index + 1) * 10,
      previousOrder: getNumericOrder(module.order),
    }))
    .filter((update) => update.previousOrder !== update.order);
};

const getSearchHaystack = (module) =>
  [
    module?.name,
    module?.sku,
    module?.description,
    module?.category,
    module?.roleLabel,
    ...(module?.serviceParentNames || []),
    ...(module?.mainParentNames || []),
    ...(module?.includedServices || []),
    ...(module?.relatedServices || []),
    ...(module?.targets || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const matchesSearch = (module, searchTerm) => {
  if (!searchTerm) {
    return true;
  }
  return getSearchHaystack(module).includes(searchTerm);
};

const getSharedByTooltipTitle = (record) => {
  const names = (record?.sharedByNames || []).filter(Boolean).join(", ");
  const title = `Presente in ${record.sharedByCount} moduli principali`;

  return names ? `${title}: ${names}` : title;
};

const RowDragContext = React.createContext(null);

const SortableRow = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"] });

  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging
      ? { position: "relative", zIndex: 9, background: "#e6f4ff" }
      : {}),
  };

  return (
    <RowDragContext.Provider value={{ setActivatorNodeRef, attributes, listeners }}>
      <tr {...props} ref={setNodeRef} style={style} />
    </RowDragContext.Provider>
  );
};

const DragHandle = () => {
  const ctx = React.useContext(RowDragContext);
  if (!ctx || !ctx.setActivatorNodeRef) {
    return (
      <Tooltip title="Rimuovi i filtri per riordinare">
        <HolderOutlined style={{ color: "#d9d9d9", cursor: "not-allowed" }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Trascina per riordinare">
      <span
        ref={ctx.setActivatorNodeRef}
        {...ctx.attributes}
        {...ctx.listeners}
        onClick={(event) => event.stopPropagation()}
        style={{
          cursor: "grab",
          color: "#8c8c8c",
          display: "inline-flex",
          touchAction: "none",
        }}
      >
        <HolderOutlined />
      </span>
    </Tooltip>
  );
};

const Modules = () => {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [globalSearch, setGlobalSearch] = useState(
    () => new URLSearchParams(location.search).get("q") || ""
  );
  const [roleFilter, setRoleFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | "active" | "inactive"
  const [unaTantumOnly, setUnaTantumOnly] = useState(false);
  const [anomaliesOnly, setAnomaliesOnly] = useState(false);
  const [recentOnly, setRecentOnly] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState(
    () => new Set([NONE_SECTION])
  );
  const [editingModule, setEditingModule] = useState(null);
  const [modalDraftSeed, setModalDraftSeed] = useState({});
  const [modalCreationContext, setModalCreationContext] = useState(null);
  const [modalSessionKey, setModalSessionKey] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [jsonViewerModule, setJsonViewerModule] = useState(null);
  const [isJsonModalVisible, setIsJsonModalVisible] = useState(false);
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const { getTokenSilently } = useAuth0();
  const graphqlUrl = `${config.baseUrl[env]}/graphql`;
  const parseDashboardUrl = config.parseDashboard[env];

  const dragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getTokenSilently();
      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query,
          variables: { where: {} },
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      const modulesData = response.data
        .map((node) => ({
          ...node,
          key: node.objectId,
          icon: node.icon || null,
          iconUrl: node.icon?.url,
          category: node.module_category_pointer?.name || "",
          categoryColor: node.module_category_pointer?.color,
          categoryDarkerColor: node.module_category_pointer?.darker_color,
          includedServices:
            node.included_service_relation?.edges.map((edge) => edge.node.name) || [],
          relatedServices:
            node.related_service_relation?.edges.map((edge) => edge.node.name) || [],
          targets: node.target_relation?.edges.map((edge) => edge.node.name) || [],
          features: node.features?.map((feature) => feature.value) || [],
        }))
        .sort(compareByOrderAndName);

      setModules(modulesData);
    } catch (error) {
      console.error("Error fetching modules:", error);
    } finally {
      setLoading(false);
    }
  }, [getTokenSilently, graphqlUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Keep the search box in sync with the URL (?q=) so filtered views are
  // shareable and linkable (e.g. from the feature edit form).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const current = params.get("q") || "";
    if (current === globalSearch) {
      return;
    }
    if (globalSearch) {
      params.set("q", globalSearch);
    } else {
      params.delete("q");
    }
    const search = params.toString();
    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : "",
    });
  }, [globalSearch, history, location.pathname, location.search]);

  const handledEditIdRef = useRef(null);

  // Deep link: ?edit=<objectId> opens that module's editor once it has loaded
  // (e.g. from the icon library on the Icons page).
  useEffect(() => {
    if (modules.length === 0) {
      return;
    }

    const editId = new URLSearchParams(location.search).get("edit");

    if (!editId || editId === handledEditIdRef.current) {
      return;
    }

    const target = modules.find((module) => module.objectId === editId);

    if (!target) {
      return;
    }

    handledEditIdRef.current = editId;
    setEditingModule(target);
    setModalDraftSeed(target);
    setModalCreationContext(null);
    setModalSessionKey((current) => current + 1);
    setIsModalVisible(true);
  }, [modules, location.search]);

  const derivedData = useMemo(() => buildModuleCatalogData(modules), [modules]);
  const stats = derivedData.stats;
  const searchTerm = globalSearch.trim().toLowerCase();

  const categoryOptions = useMemo(() => {
    const map = new Map();
    derivedData.modules.forEach((module) => {
      const cat = module.module_category_pointer;
      if (cat?.objectId) {
        map.set(cat.objectId, cat.name);
      }
    });
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [derivedData.modules]);

  const hasActiveFilter =
    !!searchTerm ||
    !!roleFilter ||
    !!categoryFilter ||
    !!statusFilter ||
    unaTantumOnly ||
    anomaliesOnly ||
    recentOnly;

  const matchesFacets = useCallback(
    (module) => {
      if (roleFilter && module.roleMeta?.key !== roleFilter) return false;
      if (
        categoryFilter &&
        module.module_category_pointer?.objectId !== categoryFilter
      )
        return false;
      if (statusFilter === "active" && !module.is_active) return false;
      if (statusFilter === "inactive" && module.is_active) return false;
      if (unaTantumOnly && !module.una_tantum) return false;
      if (anomaliesOnly && !module.hasAnomaly) return false;
      if (recentOnly && !isRecentlyChanged(module)) return false;
      if (!matchesSearch(module, searchTerm)) return false;
      return true;
    },
    [
      roleFilter,
      categoryFilter,
      statusFilter,
      unaTantumOnly,
      anomaliesOnly,
      recentOnly,
      searchTerm,
    ]
  );

  const productLineModules = useMemo(
    () =>
      derivedData.modules
        .filter((module) => module.is_product_line)
        .sort(compareByOrderAndName),
    [derivedData.modules]
  );

  const primaryServiceId = useCallback((module) => {
    const parents = module.serviceParents || [];
    if (!parents.length) {
      return NONE_SECTION;
    }
    return [...parents].sort(
      (a, b) =>
        (getNumericOrder(a.order) ?? 99999) - (getNumericOrder(b.order) ?? 99999)
    )[0].objectId;
  }, []);

  const sections = useMemo(() => {
    const groups = new Map();
    derivedData.modules.forEach((module) => {
      if (module.is_product_line) return;
      if (!matchesFacets(module)) return;
      const sid = primaryServiceId(module);
      if (!groups.has(sid)) groups.set(sid, []);
      groups.get(sid).push(module);
    });

    const orderedMeta = [
      ...productLineModules.map((pl) => ({
        id: pl.objectId,
        name: pl.name,
        sku: pl.sku,
        order: getNumericOrder(pl.order) ?? 99999,
        isActive: pl.is_active,
        productLine: pl,
        orphan: false,
      })),
      {
        id: NONE_SECTION,
        name: "Senza servizio",
        sku: "",
        order: 100000,
        isActive: true,
        productLine: null,
        orphan: true,
      },
    ].sort((a, b) => a.order - b.order);

    return orderedMeta
      .map((meta) => {
        const rows = (groups.get(meta.id) || []).sort(compareByOrderAndName);
        return {
          ...meta,
          rows,
          counts: {
            total: rows.length,
            main: rows.filter((m) => m.is_main).length,
            addon: rows.filter((m) => !m.is_main).length,
            inactive: rows.filter((m) => !m.is_active).length,
            anomalies: rows.filter((m) => m.hasAnomaly).length,
          },
        };
      })
      .filter((section) => section.rows.length > 0);
  }, [derivedData.modules, matchesFacets, primaryServiceId, productLineModules]);

  const shownCount = useMemo(
    () => sections.reduce((acc, section) => acc + section.rows.length, 0),
    [sections]
  );

  const visibleSectionIds = sections.map((section) => section.id);
  const activeSectionKeys = hasActiveFilter
    ? visibleSectionIds
    : visibleSectionIds.filter((id) => !collapsedSections.has(id));

  const handleSectionChange = (keys) => {
    if (hasActiveFilter) return;
    const open = new Set(Array.isArray(keys) ? keys : [keys]);
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      visibleSectionIds.forEach((id) => {
        if (open.has(id)) next.delete(id);
        else next.add(id);
      });
      return next;
    });
  };

  // ---- handlers ----
  const handleEdit = (record) => {
    setEditingModule(record);
    setModalDraftSeed(record);
    setModalCreationContext(null);
    setModalSessionKey((current) => current + 1);
    setIsModalVisible(true);
  };

  const handleNew = () => {
    setEditingModule(null);
    setModalDraftSeed({});
    setModalCreationContext(null);
    setModalSessionKey((current) => current + 1);
    setIsModalVisible(true);
  };

  const handleQuickCreate = ({
    source,
    linkParent,
    productLine,
    category,
    relationField,
    modulesScope,
    isMain,
  }) => {
    const categorySeed = getCategorySeed(
      category || linkParent?.module_category_pointer
    );
    const creationContext = {
      source,
      linkParentId: linkParent?.objectId,
      linkParentName: linkParent?.name,
      linkRelation: relationField,
      serviceId: productLine?.objectId,
      serviceName: productLine?.name,
      categoryId: categorySeed?.objectId,
      categoryName: categorySeed?.name,
      defaultOrder: getNextOrderValue(modulesScope),
    };

    setEditingModule(null);
    setModalDraftSeed({
      name: "",
      sku: "",
      description: "",
      price: 0,
      minutes_of_training: 0,
      is_product_line: false,
      is_main: isMain,
      una_tantum: false,
      power_by: "",
      power_by_link: "",
      order: creationContext.defaultOrder,
      module_category_pointer: categorySeed,
      quantity: false,
      included_quantity: 0,
      charge_by_quantity: false,
      price_for_quantity: 0,
      unit_of_measure: "",
      is_active: true,
      related_service_relation: { edges: [] },
      included_service_relation: { edges: [] },
      target_relation: { edges: [] },
      included_quantity_max_step: false,
      included_quantity_max_step_multiplier: 1,
      included_quantity_max_step_minimum: 0,
      youtube_video_url: "",
      landing_page_url: "",
      cover_image_url: "",
      features: [],
      html_description: "",
    });
    setModalCreationContext(creationContext);
    setModalSessionKey((current) => current + 1);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setEditingModule(null);
    setModalCreationContext(null);
    setIsModalVisible(false);
  };

  const handleSuccess = () => {
    setEditingModule(null);
    setModalCreationContext(null);
    setIsModalVisible(false);
    fetchData();
  };

  const handleClone = (record) => {
    const clonedModule = {
      ...record,
      name: `${record.name} (Copy)`,
      sku: "",
      objectId: null,
    };
    setEditingModule(clonedModule);
    setModalDraftSeed(clonedModule);
    setModalCreationContext(null);
    setModalSessionKey((current) => current + 1);
    setIsModalVisible(true);
  };

  const handleSectionDragEnd = useCallback(
    async (sectionRows, { active, over }) => {
      if (!active?.id || !over?.id || active.id === over.id) {
        return;
      }
      const fromIndex = sectionRows.findIndex((m) => m.objectId === active.id);
      const toIndex = sectionRows.findIndex((m) => m.objectId === over.id);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
        return;
      }
      const updates = buildModuleOrderUpdates(sectionRows, fromIndex, toIndex);
      if (updates.length === 0) {
        return;
      }

      // Optimistic update: apply the new order locally right away so the row
      // stays where it was dropped (no snap-back, no full-page reload).
      const orderById = new Map(updates.map((update) => [update.id, update.order]));
      setModules((prev) =>
        prev.map((module) =>
          orderById.has(module.objectId)
            ? { ...module, order: orderById.get(module.objectId) }
            : module
        )
      );

      try {
        const token = await getTokenSilently();

        await Promise.all(
          updates.map((update) =>
            axios({
              method: "post",
              url: graphqlUrl,
              data: {
                query: updateModuleOrderMutation,
                variables: {
                  id: update.id,
                  fields: { order: update.order },
                },
              },
              headers: { Authorization: `Bearer ${token}` },
            }).then((response) => {
              if (response.data.errors) {
                throw new Error(
                  response.data.errors.map((error) => error.message).join(", ")
                );
              }
            })
          )
        );

        message.success("Ordine aggiornato");
      } catch (error) {
        console.error("Error reordering modules:", error);
        message.error(error.message || "Impossibile aggiornare l'ordine");
        // Roll back to server truth on failure.
        fetchData();
      }
    },
    [fetchData, getTokenSilently, graphqlUrl]
  );

  // ---- render helpers ----
  const renderIcon = (iconUrl, alt = "module icon", size = 28) =>
    iconUrl ? (
      <img
        src={iconUrl}
        alt={alt}
        style={{ width: size, height: size, objectFit: "contain", verticalAlign: "middle" }}
      />
    ) : (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: 8,
          background:
            "linear-gradient(135deg, rgba(25,92,126,0.12), rgba(29,171,172,0.08))",
          border: "1px solid rgba(25,92,126,0.08)",
        }}
      />
    );

  const renderHighlight = (text) => {
    const value = text ? text.toString() : "";
    if (!searchTerm) return value;
    return (
      <Highlighter
        highlightStyle={{ backgroundColor: "#ffe58f", padding: 0 }}
        searchWords={[globalSearch]}
        autoEscape
        textToHighlight={value}
      />
    );
  };

  const buildColumns = () => [
    {
      title: "#",
      key: "order",
      width: 80,
      render: (_, record) => (
        <Space size={8}>
          <DragHandle />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {getOrderDisplay(record.order)}
          </Text>
        </Space>
      ),
    },
    {
      title: "Modulo",
      dataIndex: "name",
      key: "name",
      width: "26%",
      render: (name, record) => (
        <Space>
          {renderIcon(record.iconUrl, record.name, 26)}
          <Space size={6}>
            <span
              style={{
                fontWeight: 500,
                color: record.is_active ? undefined : "rgba(0,0,0,.45)",
                textDecoration: record.is_active ? "none" : "line-through",
              }}
            >
              {renderHighlight(name)}
            </span>
            <Tag color={record.roleMeta?.color}>{record.roleLabel}</Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: "Categoria",
      key: "category",
      width: "12%",
      render: (_, record) =>
        record.module_category_pointer ? (
          <Tag color={record.module_category_pointer.darker_color || "default"}>
            {record.module_category_pointer.name}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
      width: "15%",
      render: (sku) =>
        sku ? (
          <Text code style={{ fontSize: 12 }}>
            {renderHighlight(sku)}
          </Text>
        ) : (
          <Text style={{ color: WARN_COLOR, fontSize: 12 }}>⚠ —</Text>
        ),
    },
    {
      title: "Prezzo",
      dataIndex: "price",
      key: "price",
      width: "15%",
      render: (price, record) => {
        const label = getPriceLabel(price);
        if (!label) return <Text type="secondary">—</Text>;
        return (
          <Space size={4} wrap>
            <Text strong>{label}</Text>
            {record.una_tantum ? (
              <Tag color="gold" style={{ marginInlineEnd: 0 }}>
                una tantum
              </Tag>
            ) : (
              <Text type="secondary" style={{ fontSize: 11 }}>
                /mese
              </Text>
            )}
            {record.charge_by_quantity && (
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                a quantità
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "Relazioni",
      key: "relations",
      width: "12%",
      render: (_, record) => {
        const parts = [];
        if (record.includedCount > 0)
          parts.push(
            <Tooltip key="inc" title={`Include ${record.includedCount} moduli`}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ⊃ {record.includedCount}
              </Text>
            </Tooltip>
          );
        if (record.relatedCount > 0)
          parts.push(
            <Tooltip key="rel" title={`${record.relatedCount} moduli correlati`}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ~ {record.relatedCount}
              </Text>
            </Tooltip>
          );
        if (record.sharedByCount > 0)
          parts.push(
            <Tooltip key="in" title={getSharedByTooltipTitle(record)}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ▲ {record.sharedByCount}
              </Text>
            </Tooltip>
          );
        return parts.length ? <Space size={8}>{parts}</Space> : <Text type="secondary">—</Text>;
      },
    },
    {
      title: <Tooltip title="Anomalie">⚠</Tooltip>,
      key: "anomalies",
      width: 48,
      align: "center",
      render: (_, record) =>
        record.hasAnomaly ? (
          <Tooltip
            title={(record.anomalyFlags || [])
              .map((type) => ANOMALY_META[type]?.label || type)
              .join(", ")}
          >
            <span style={{ color: WARN_COLOR }}>⚠</span>
          </Tooltip>
        ) : null,
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size={0} onClick={(event) => event.stopPropagation()}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              handleEdit(record);
            }}
          />
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              handleClone(record);
            }}
          />
          <Button
            type="text"
            size="small"
            icon={<BorderlessTableOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              setJsonViewerModule(record);
              setIsJsonModalVisible(true);
            }}
          />
          <a
            href={`${parseDashboardUrl}/browser/module?filters=[{"field"%3A"objectId","constraint"%3A"eq","compareTo"%3A"${record.objectId}"}]`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            style={{ display: "inline-flex", alignItems: "center", padding: "0 6px" }}
          >
            <img
              src={ParseLogo}
              alt="Parse Dashboard"
              style={{ width: 18, height: 18, objectFit: "contain" }}
            />
          </a>
        </Space>
      ),
    },
  ];

  // ---- KPI strip ----
  const kpis = [
    { key: "", label: "Moduli totali", value: stats.total, sub: `${stats.productLines} servizi` },
    { key: "role:main", label: "Principali", value: stats.mainModules },
    { key: "role:addon", label: "Aggiuntivi", value: stats.addonModules },
    { key: "inactive", label: "Inattivi", value: stats.inactive, warn: true },
    { key: "una", label: "Una tantum", value: stats.oneTimeModules },
    { key: "anom", label: "Anomalie", value: stats.anomalies, warn: true },
  ];

  const isKpiActive = (key) =>
    (key === "role:main" && roleFilter === "main") ||
    (key === "role:addon" && roleFilter === "addon") ||
    (key === "inactive" && statusFilter === "inactive") ||
    (key === "una" && unaTantumOnly) ||
    (key === "anom" && anomaliesOnly);

  const onKpiClick = (key) => {
    if (key === "role:main") setRoleFilter((v) => (v === "main" ? "" : "main"));
    else if (key === "role:addon") setRoleFilter((v) => (v === "addon" ? "" : "addon"));
    else if (key === "inactive")
      setStatusFilter((v) => (v === "inactive" ? "" : "inactive"));
    else if (key === "una") setUnaTantumOnly((v) => !v);
    else if (key === "anom") setAnomaliesOnly((v) => !v);
  };

  const resetFilters = () => {
    setGlobalSearch("");
    setRoleFilter("");
    setCategoryFilter("");
    setStatusFilter("");
    setUnaTantumOnly(false);
    setAnomaliesOnly(false);
    setRecentOnly(false);
  };

  const renderSectionTable = (section) => {
    const table = (
      <Table
        columns={buildColumns()}
        dataSource={section.rows}
        rowKey="objectId"
        size="small"
        pagination={false}
        components={hasActiveFilter ? undefined : { body: { row: SortableRow } }}
        onRow={(record) => ({
          style: { cursor: "pointer" },
          onClick: () => handleEdit(record),
        })}
      />
    );

    if (hasActiveFilter) {
      return table;
    }

    return (
      <DndContext
        sensors={dragSensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => handleSectionDragEnd(section.rows, event)}
      >
        <SortableContext
          items={section.rows.map((module) => module.objectId)}
          strategy={verticalListSortingStrategy}
        >
          {table}
        </SortableContext>
      </DndContext>
    );
  };

  const collapseItems = sections.map((section) => ({
    key: section.id,
    label: (
      <Space>
        <Text strong style={{ fontSize: 15 }}>
          {section.name}
        </Text>
        {section.orphan && <Tag color="gold">orfani</Tag>}
        {!section.isActive && <Tag>inattivo</Tag>}
        {section.sku && (
          <Text code style={{ fontSize: 11 }}>
            {section.sku}
          </Text>
        )}
      </Space>
    ),
    extra: (
      <Space size={6} onClick={(event) => event.stopPropagation()}>
        <Tag>{section.counts.total} moduli</Tag>
        <Tag color="purple">{section.counts.main} principali</Tag>
        <Tag color="orange">{section.counts.addon} aggiuntivi</Tag>
        {section.counts.inactive > 0 && (
          <Tag color="default">{section.counts.inactive} inattivi</Tag>
        )}
        {section.counts.anomalies > 0 && (
          <Tag color="warning">⚠ {section.counts.anomalies}</Tag>
        )}
        {section.productLine && (
          <Button
            size="small"
            type="text"
            icon={<EditOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              handleEdit(section.productLine);
            }}
          />
        )}
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            if (section.productLine) {
              handleQuickCreate({
                source: "section",
                linkParent: section.productLine,
                productLine: section.productLine,
                relationField: "related_service_relation",
                modulesScope: section.rows,
                isMain: true,
              });
            } else {
              handleNew();
            }
          }}
        >
          modulo
        </Button>
      </Space>
    ),
    children: renderSectionTable(section),
  }));

  return (
    <div style={{ margin: "16px 16px 40px" }}>
      <Spin spinning={loading}>
        <Breadcrumb
          style={{ marginBottom: 16 }}
          items={[{ title: "Configurazioni" }, { title: "Moduli" }]}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            marginBottom: 16,
          }}
        >
          <div>
            <Title level={3} style={{ margin: 0 }}>
              Moduli
            </Title>
            <Text type="secondary">
              Catalogo dei moduli vendibili — gerarchia Servizio → Principale →
              Aggiuntivo, relazioni di inclusione/correlazione, pricing e SKU.
            </Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleNew}>
            Nuovo Modulo
          </Button>
        </div>

        {/* KPI strip */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 12,
            marginBottom: 16,
          }}
        >
          {kpis.map((kpi) => {
            const clickable = kpi.key !== "";
            const active = isKpiActive(kpi.key);
            return (
              <Card
                key={kpi.label}
                size="small"
                hoverable={clickable}
                onClick={clickable ? () => onKpiClick(kpi.key) : undefined}
                style={{
                  cursor: clickable ? "pointer" : "default",
                  borderColor: active ? "#1677ff" : undefined,
                  background: active ? "#f0f7ff" : undefined,
                }}
                styles={{ body: { padding: "12px 14px" } }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: kpi.warn ? WARN_COLOR : "rgba(0,0,0,.45)",
                    marginBottom: 4,
                  }}
                >
                  {kpi.warn && (
                    <span
                      style={{
                        display: "inline-block",
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: WARN_COLOR,
                        marginRight: 6,
                      }}
                    />
                  )}
                  {kpi.label}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 600,
                    lineHeight: 1.1,
                    color: kpi.warn ? WARN_COLOR : undefined,
                  }}
                >
                  {kpi.value}
                  {kpi.sub && (
                    <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
                      {" "}
                      · {kpi.sub}
                    </Text>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Toolbar */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 10,
            marginBottom: 12,
          }}
        >
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Cerca per nome, SKU, relazione..."
            value={globalSearch}
            onChange={(event) => setGlobalSearch(event.target.value)}
            style={{ width: 320 }}
          />
          <Segmented
            value={roleFilter}
            onChange={setRoleFilter}
            options={ROLE_FILTER_OPTIONS}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Categoria"
            value={categoryFilter || undefined}
            onChange={(value) => setCategoryFilter(value || "")}
            options={categoryOptions}
            style={{ width: 200 }}
          />
          <Tooltip title="Solo moduli attivi">
            <Space size={6}>
              <Switch
                size="small"
                checked={statusFilter === "active"}
                onChange={(checked) => setStatusFilter(checked ? "active" : "")}
              />
              <Text type="secondary">Solo attivi</Text>
            </Space>
          </Tooltip>
          <Tooltip title="Solo moduli una tantum">
            <Space size={6}>
              <Switch size="small" checked={unaTantumOnly} onChange={setUnaTantumOnly} />
              <Text type="secondary">Una tantum</Text>
            </Space>
          </Tooltip>
          <Tooltip title="Solo moduli con anomalie">
            <Space size={6}>
              <Switch size="small" checked={anomaliesOnly} onChange={setAnomaliesOnly} />
              <Text type="secondary">⚠ Anomalie</Text>
            </Space>
          </Tooltip>
          <Tooltip title={`Creati o aggiornati negli ultimi ${RECENT_DAYS} giorni`}>
            <Space size={6}>
              <Switch size="small" checked={recentOnly} onChange={setRecentOnly} />
              <Text type="secondary">Recenti</Text>
            </Space>
          </Tooltip>
          <div style={{ flex: 1 }} />
          <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
            {shownCount} moduli · {sections.length} sezioni
          </Text>
          {hasActiveFilter && (
            <Button type="link" onClick={resetFilters} style={{ padding: 0 }}>
              Azzera
            </Button>
          )}
        </div>

        {sections.length === 0 ? (
          <Empty description="Nessun modulo corrisponde ai filtri." style={{ padding: 48 }} />
        ) : (
          <Collapse
            items={collapseItems}
            activeKey={activeSectionKeys}
            onChange={handleSectionChange}
          />
        )}

        <ModuleEditModal
          visible={isModalVisible}
          module={editingModule}
          draftSeed={modalDraftSeed}
          creationContext={modalCreationContext}
          sessionKey={modalSessionKey}
          onCancel={handleCancel}
          onSuccess={handleSuccess}
        />
        <Modal
          title="Module JSON Data"
          open={isJsonModalVisible}
          onCancel={() => setIsJsonModalVisible(false)}
          width={800}
          footer={null}
          style={{ top: 20 }}
        >
          {jsonViewerModule && (
            <ReactJson
              src={jsonViewerModule}
              collapseStringsAfterLength={30}
              name={false}
              collapsed={1}
              displayDataTypes={true}
              displayObjectSize={false}
              enableClipboard={false}
              style={{ maxHeight: "89vh", overflow: "auto" }}
            />
          )}
        </Modal>
      </Spin>
    </div>
  );
};

export default Modules;
